/**
 * Maatwerk: offertelijst feature
 * Offertelijst (Quote List) Plugin
 * Vergelijkbaar met compare functionaliteit maar voor offerte aanvragen
 */

class QuoteList {
    constructor() {
      this.storageKey = 'quote_list';
      this.maxItems = 50; // Maximum aantal items in offertelijst
      this.quantityUpdateTimeout = null; // For debouncing quantity updates
      this.init();
    }
  
    init() {
      // Initialiseer event listeners
      this.attachEventListeners();
      // Herstel checkboxes uit localStorage
      this.restoreCheckboxes();
      // Update checkbox states en buttons
      this.updateCheckboxStates();
      this.updateButtons();
      // Render de quote list drawer
      this.renderQuoteListDrawer();
      // Update header count
      this.updateHeaderCount();
    }
  
    attachEventListeners() {
  
      // Event delegation voor dynamisch toegevoegde checkboxes (product cards)
      document.addEventListener('change', (e) => {
        if (e.target.classList.contains('quote-checkbox')) {
          this.handleCheckboxChange(e.target);
        }
        
        
        // Handle quantity changes in drawer - multiple checks for reliability
        if (e.target.classList.contains('quote-quantity-input') || 
            e.target.matches('.quote-quantity-input') ||
            e.target.closest('.quote-list-items')) {
          if (e.target.classList.contains('quote-quantity-input')) {
            this.handleQuantityChange(e.target);
          }
        }
      });
  
      // Also listen for input events for real-time quantity updates
      document.addEventListener('input', (e) => {
        if (e.target.classList.contains('quote-quantity-input') || 
            e.target.matches('.quote-quantity-input')) {
          this.handleQuantityChange(e.target);
        }
      });
  
      // Listen for keyup events as well (for better responsiveness)
      document.addEventListener('keyup', (e) => {
        if (e.target.classList.contains('quote-quantity-input') || 
            e.target.matches('.quote-quantity-input')) {
          this.handleQuantityChange(e.target);
        }
      });
  
      // Additional event listener specifically for the quote list drawer
      const setupDrawerEventListeners = () => {
        const drawer = document.getElementById('quote-list');
        if (drawer) {
          drawer.addEventListener('change', (e) => {
            if (e.target.classList.contains('quote-quantity-input')) {
              this.handleQuantityChange(e.target);
            }
          });
          
          drawer.addEventListener('input', (e) => {
            if (e.target.classList.contains('quote-quantity-input')) {
              this.handleQuantityChange(e.target);
            }
          });
        }
      };
      
      // Set up drawer listeners now and after DOM content loaded
      setupDrawerEventListeners();
      document.addEventListener('DOMContentLoaded', setupDrawerEventListeners.bind(this));
  
      // Maatwerk: Listen for themeVariantUpdated event (product page variant changes)
      window.addEventListener('themeVariantUpdated', (e) => {
        this.handleVariantChangeProductPage();
      });
  
      // Separate event listener for product card variant changes
      this.attachVariantCardListeners();
      
      // Re-attach variant card listeners when DOM content is loaded (for dynamically loaded content)
      document.addEventListener('DOMContentLoaded', () => {
        this.attachVariantCardListeners();
      });
  
      // Event delegation voor quote buttons (product pages)
      document.addEventListener('click', (e) => {
        if (e.target.classList.contains('quote-button') || e.target.closest('.quote-button')) {
          const button = e.target.classList.contains('quote-button') ? e.target : e.target.closest('.quote-button');
          this.handleButtonClick(button);
        }
        
        // Handle remove buttons in drawer and page
        if (e.target.classList.contains('quote-remove') || e.target.closest('.quote-remove')) {
          const button = e.target.classList.contains('quote-remove') ? e.target : e.target.closest('.quote-remove');
          const productId = button.dataset.productId;
          this.removeFromQuoteList(productId);
          this.renderQuoteListDrawer();
          this.renderQuoteListPage(); // Maatwerk: render page if on quote list page
          this.updateCheckboxStates();
          this.updateButtons();
          this.updateHeaderCount();
          this.updateContactFormVisibility(); // Maatwerk: update contact form visibility
          this.updateClearButtonVisibility(); // Maatwerk: update clear button visibility
        }
        
        // Handle clear all button (both drawer and page)
        if (e.target.classList.contains('quote-clear-all') || e.target.closest('.quote-clear-all') ||
            e.target.classList.contains('quote-clear-all-page') || e.target.closest('.quote-clear-all-page')) {
          e.preventDefault(); // Prevent default link behavior
          
          // Get confirm settings based on button type
          const confirmSettings = this.getClearConfirmSettings(e.target);
          
          // Show confirm dialog if enabled, otherwise clear directly
          if (!confirmSettings.showConfirm || confirm(confirmSettings.confirmText)) {
            this.clearQuoteList();
            this.renderQuoteListDrawer();
            this.renderQuoteListPage(); // Maatwerk: render page if on quote list page
            this.updateCheckboxStates();
            this.updateButtons();
            this.updateHeaderCount();
            this.updateContactFormVisibility(); // Maatwerk: update contact form visibility
            this.updateClearButtonVisibility(); // Maatwerk: update clear button visibility
          }
        }
        
        // Handle quote list toggle button (drawer open/close)
        if (e.target.classList.contains('quote-list-toggle') || e.target.closest('.quote-list-toggle')) {
          this.toggleQuoteListDrawer();
        }
        
        // Handle data-panel="quote-list" links
        if (e.target.matches('[data-panel="quote-list"]') || e.target.closest('[data-panel="quote-list"]')) {
          e.preventDefault();
          this.toggleQuoteListDrawer();
        }
        
        // Close drawer when clicking on overlay
        if (e.target.id === 'quote-list' && e.target.getAttribute('aria-hidden') === 'false') {
          this.toggleQuoteListDrawer();
        }
      });
  
      // Luister naar page loads om checkboxes en buttons te herstellen
      document.addEventListener('DOMContentLoaded', () => {
        this.restoreCheckboxes();
        this.updateButtons();
      });
    }
  
    handleCheckboxChange(checkbox) {
      const productData = this.getProductDataFromCheckbox(checkbox);
      
      // Get variant info from the product card and checkbox itself
      const productCard = checkbox.closest('.product-item, .product-card, .l4c, li[data-product-id], article[data-product-id], .twn-starter__products-grid-item, .twn-product-tile');
      
      const variantInfo = this.getCurrentVariantInfo(productCard, null, checkbox);
      
      // Create complete item data with variant info
      const itemData = {
        ...productData,
        variantId: variantInfo.id,
        variantTitle: variantInfo.title,
        variantImage: variantInfo.image
      };
      
      const uniqueId = this.getUniqueItemId(productData.id, variantInfo.id);
      
      if (checkbox.checked) {
        this.addToQuoteList(itemData);
      } else {
        this.removeFromQuoteList(uniqueId);
      }
      
      // Don't call updateCheckboxStates() here as it will overwrite the current change
      // Instead, just update the current checkbox to reflect the storage state
      const uniqueIdForCheck = this.getUniqueItemId(productData.id, variantInfo.id);
      const isNowInList = this.isItemInQuoteList(uniqueIdForCheck);
      checkbox.checked = isNowInList;
      this.updateCheckboxVisuals(checkbox, isNowInList);
      
      // No need to sync ALL checkboxes since variants are individual
      this.renderQuoteListDrawer();
      this.renderQuoteListPage(); // Maatwerk: render page if on quote list page
      this.updateHeaderCount();
      this.updateContactFormVisibility(); // Maatwerk: update contact form visibility
      this.updateClearButtonVisibility(); // Maatwerk: update clear button visibility
    }
  
    attachVariantCardListeners() {
      // Find all product card variant selectors using the specific selector from custom-async.js
      // Only select elements that don't already have our quote-list listener
      const variantSelectors = document.querySelectorAll('.l4cl .product-card.update-variants select[name="id"]:not(.quote-listening), .l4cl .product-card.update-variants input[name="id"]:not(.quote-listening)');
      
      variantSelectors.forEach(selector => {
        // Mark this element as having our listener
        selector.classList.add('quote-listening');
        
        selector.addEventListener('change', (e) => {
          this.handleVariantChangeCard(e.target);
        });
      });
    }
  
    // Public method to re-initialize variant listeners (e.g., after AJAX content loads)
    reinitializeVariantListeners() {
      this.attachVariantCardListeners();
    }
  
    handleQuantityChange(input) {
      const dataProductId = input.dataset.productId;
      const variantId = input.dataset.variantId || null;
      const newQuantity = parseInt(input.value) || 1;
      
      if (!dataProductId) {
        console.error('No product ID found on quantity input:', input);
        return;
      }
      
      // Check if dataProductId is already a unique ID (contains underscore + variant)
      let productId, actualVariantId;
      
      if (dataProductId.includes('_') && variantId) {
        // dataProductId is likely a unique ID format: "productId_variantId"
        // Split it to get the actual product ID
        const parts = dataProductId.split('_');
        if (parts.length === 2 && parts[1] === variantId) {
          productId = parts[0];
          actualVariantId = variantId;
        } else {
          // Fallback: treat as regular product ID
          productId = dataProductId;
          actualVariantId = variantId;
        }
      } else {
        // dataProductId is a regular product ID
        productId = dataProductId;
        actualVariantId = variantId;
      }
      
      // Update localStorage using the correct IDs
      const finalUniqueId = this.getUniqueItemId(productId, actualVariantId);
      this.updateQuoteListItemQuantity(productId, Math.max(1, newQuantity), actualVariantId);
      
      // Update buttons on other pages
      this.updateButtons();
      
      // Update header count
      this.updateHeaderCount();
      
      // Update drawer and page content
      this.renderQuoteListDrawer(); // Maatwerk: update drawer when quantity changes
      this.renderQuoteListPage(); // Maatwerk: update page when quantity changes
      
      // Update contact form visibility
      this.updateContactFormVisibility();
      this.updateClearButtonVisibility(); // Maatwerk: update clear button visibility
      
      // Debounce to avoid too many updates
      clearTimeout(this.quantityUpdateTimeout);
      this.quantityUpdateTimeout = setTimeout(() => {
        // Quantity update completed
      }, 300);
    }
  
    // Maatwerk: Variant detection functions
    getCurrentVariantInfo(productCard = null, productButton = null, checkbox = null) {
      let variantId = null;
      let variantTitle = null;
  
      // Method 0: From checkbox data attributes (highest priority - when checkbox is provided directly)
      if (checkbox) {
        const currentVariantId = checkbox.dataset.currentVariantId;
        const currentVariantTitle = checkbox.dataset.currentVariantTitle;
        const currentVariantImage = checkbox.dataset.currentVariantImage;
        
        // Check if checkbox has variant data
        if (currentVariantId && currentVariantId !== 'undefined' && currentVariantId !== '') {
          return { 
            id: currentVariantId, 
            title: currentVariantTitle || null, 
            image: currentVariantImage || null 
          };
        }
      }
  
      // Method 1: From product button data attributes (product page - after variant change)
      if (productButton) {
        const currentVariantId = productButton.dataset.currentVariantId;
        const currentVariantTitle = productButton.dataset.currentVariantTitle;
        const currentVariantImage = productButton.dataset.currentVariantImage;
        
        // Check if either ID or title exists (title might be empty for some variants)
        if (currentVariantId && currentVariantId !== 'undefined' && currentVariantId !== '') {
          return { 
            id: currentVariantId, 
            title: currentVariantTitle || null, 
            image: currentVariantImage || null 
          };
        }
      }
  
      // Method 1.5: From product card checkbox data attributes (when checkbox has variant info)
      if (productCard) {
        const checkbox = productCard.querySelector('.quote-checkbox');
        if (checkbox) {
          const currentVariantId = checkbox.dataset.currentVariantId;
          const currentVariantTitle = checkbox.dataset.currentVariantTitle;
          const currentVariantImage = checkbox.dataset.currentVariantImage;
          
          // Check if checkbox has variant data
          if (currentVariantId && currentVariantId !== 'undefined' && currentVariantId !== '') {
            return { 
              id: currentVariantId, 
              title: currentVariantTitle || null, 
              image: currentVariantImage || null 
            };
          }
        }
      }
  
      // Method 2: From product card variant selector (variant info in option data-variantinfo)
      if (productCard) {
        // Use specific selector for product card variant selectors (matching custom-async.js)
        // Include both quote-listening and non-quote-listening elements for detection
        const variantSelect = productCard.querySelector('.update-variants select[name="id"], .update-variants input[name="id"]');
        if (variantSelect && variantSelect.value) {
          
          // Handle select dropdown (with options)
          if (variantSelect.tagName === 'SELECT') {
            const selectedOption = variantSelect.querySelector(`option[value="${variantSelect.value}"]`);
            if (selectedOption) {
              const variantInfoData = selectedOption.getAttribute('data-variantinfo');
              if (variantInfoData) {
                try {
                  const variantInfo = JSON.parse(variantInfoData);
                  
                  // Get title from option text (since JSON might not have title)
                  const variantTitle = selectedOption.textContent ? selectedOption.textContent.trim() : null;
                  const variantImage = variantInfo.image || null;
                  
                  return { 
                    id: variantSelect.value, 
                    title: variantTitle,
                    image: variantImage
                  };
                } catch (e) {
                  console.error('❌ Method 2: Error parsing variant info data:', e);
                }
              }
              
              // Fallback to option text if data-variantinfo fails
              if (selectedOption.textContent && selectedOption.textContent.trim()) {
                const variantTitle = selectedOption.textContent.trim();
                return { id: variantSelect.value, title: variantTitle, image: null };
              }
            }
          }
          // Handle hidden input (no options, just ID)
          else if (variantSelect.tagName === 'INPUT' && variantSelect.type === 'hidden') {
            // Hidden inputs usually don't have title/image info, so return just the ID
            // This prevents fallback to Method 3 which could pick up wrong variant data from other products
            return { 
              id: variantSelect.value, 
              title: null, 
              image: null 
            };
          }
        }
      }
  
      // Method 3: From product page variant selector (general fallback - but only if we're not dealing with a product card)
      if (!productCard) {
        const variantSelectors = [
          'select[name="id"]', // General variant selector
          'input[name="id"]:checked', // Radio button variant selector
          '[data-selected-variant]' // Any element with selected variant data
        ];
  
      for (const selector of variantSelectors) {
        const element = document.querySelector(selector);
        if (element && element.value) {
          
          // For select elements, get variant info from option data-variantinfo
          if (element.tagName === 'SELECT') {
            const selectedOption = element.querySelector(`option[value="${element.value}"]`);
            if (selectedOption) {
              // First try data-variantinfo attribute
              const variantInfoData = selectedOption.getAttribute('data-variantinfo');
              if (variantInfoData) {
                try {
                  const variantInfo = JSON.parse(variantInfoData);
                  
                  // Get title from option text (since JSON might not have title)
                  const variantTitle = selectedOption.textContent ? selectedOption.textContent.trim() : null;
                  const variantImage = variantInfo.image || null;
                  
                  return { 
                    id: element.value, 
                    title: variantTitle,
                    image: variantImage
                  };
                } catch (e) {
                  console.error('❌ Method 3: Error parsing variant info data:', e);
                }
              }
              
              // Fallback to option text
              if (selectedOption.textContent && selectedOption.textContent.trim()) {
                const variantTitle = selectedOption.textContent.trim();
                return { id: element.value, title: variantTitle, image: null };
              }
            }
          }
          
          // For radio buttons, try to find associated label
          if (element.tagName === 'INPUT' && element.type === 'radio') {
            const label = document.querySelector(`label[for="${element.id}"]`);
            if (label && label.textContent && label.textContent.trim()) {
              const variantTitle = label.textContent.trim();
              return { id: element.value, title: variantTitle, image: null };
            }
          }
        }
      }
      }
  
      // Method 4: From URL or page data (last resort)
      const urlParams = new URLSearchParams(window.location.search);
      const variantFromUrl = urlParams.get('variant');
      if (variantFromUrl) {
        return { id: variantFromUrl, title: null, image: null }; // Title and image will need to be fetched separately
      }
  
      return { id: null, title: null, image: null };
    }
  
    getCurrentVariantFromDOM() {
      // Method 1: Check radio buttons (most common on product pages)
      const checkedRadio = document.querySelector('input[name="id"]:checked');
      if (checkedRadio && checkedRadio.value) {
        const variantId = checkedRadio.value;
        const variantTitle = checkedRadio.title || checkedRadio.getAttribute('title') || null;
        return { id: variantId, title: variantTitle, image: null }; // Radio buttons don't have image data
      }
      
      // Method 2: Check select dropdown
      const variantSelect = document.querySelector('select[name="id"]');
      if (variantSelect && variantSelect.value) {
        const selectedOption = variantSelect.querySelector(`option[value="${variantSelect.value}"]`);
        if (selectedOption) {
          const variantId = variantSelect.value;
          let variantTitle = null;
          let variantImage = null;
          
          // Try to get title and image from data-variantinfo
          const variantInfoData = selectedOption.getAttribute('data-variantinfo');
          if (variantInfoData) {
            try {
              const variantInfo = JSON.parse(variantInfoData);
              // Get title from option text since JSON might not have title
              variantTitle = selectedOption.textContent ? selectedOption.textContent.trim() : null;
              variantImage = variantInfo.image;
              return { id: variantId, title: variantTitle, image: variantImage };
            } catch (e) {
              // Fallback to option text
              variantTitle = selectedOption.textContent ? selectedOption.textContent.trim() : null;
            }
          } else {
            // Fallback to option text
            variantTitle = selectedOption.textContent ? selectedOption.textContent.trim() : null;
          }
          
          return { id: variantId, title: variantTitle, image: null };
        }
      }
      
      // Method 3: Check hidden inputs
      const hiddenVariantInput = document.querySelector('input[name="id"][type="hidden"]');
      if (hiddenVariantInput && hiddenVariantInput.value) {
        return { id: hiddenVariantInput.value, title: null, image: null };
      }
      
      return { id: null, title: null, image: null };
    }
  
    handleVariantChangeCard(variantSelect) {
      const productCard = variantSelect.closest('.product-item, .product-card, .l4c, li[data-product-id], article[data-product-id]');
      if (!productCard) return;
  
      // Update checkbox states based on new variant
      const variantInfo = this.getCurrentVariantInfo(productCard);
      if (variantInfo.id) {
        this.updateCardCheckboxState(productCard, variantInfo);
      }
    }
  
    handleVariantChangeProductPage() {
      // Small delay to let button data attributes update first
      setTimeout(() => {
        
        // Find the quote button and re-initialize it
        const quoteButtons = document.querySelectorAll('.quote-button');
        quoteButtons.forEach(button => {
          // Reset button text if this variant is not in the list
          const productData = this.getProductDataFromButton(button);
          
          // Always check DOM directly first (button data attributes might be stale)
          let variantInfo = this.getCurrentVariantFromDOM();
          
          // If DOM check failed, try button data attributes as fallback
          if (!variantInfo.id) {
            variantInfo = this.getCurrentVariantInfo(null, button);
          }
          
          if (variantInfo.id) {
            const uniqueId = this.getUniqueItemId(productData.id, variantInfo.id);
            const specificItem = this.findItemInQuoteList(uniqueId);
            
            // Reset button text based on whether this specific variant is in the list
            const addText = button.dataset.textAdd || 'Toevoegen aan offertelijst';
            const addedTextTemplate = button.dataset.textAdded || '{quantity}x toegevoegd aan offertelijst';
            
            const buttonTextElement = button.querySelector('.quote-button-text');
            if (buttonTextElement) {
              if (specificItem) {
                // This specific variant is in the list - replace {quantity} placeholder
                const finalText = addedTextTemplate.replace('{quantity}', specificItem.quantity);
                buttonTextElement.textContent = finalText;
                button.classList.add('in-quote-list');
              } else {
                // This specific variant is not in the list
                buttonTextElement.textContent = addText;
                button.classList.remove('in-quote-list');
              }
            }
          }
        });
      }, 100); // Small delay to let DOM update
    }
  
    getUniqueItemId(productId, variantId = null) {
      // Create unique ID combining product ID and variant ID
      return variantId ? `${productId}_${variantId}` : productId.toString();
    }
  
    findItemInQuoteList(uniqueId) {
      const quoteList = this.getQuoteList();
      return quoteList.find(item => {
        const itemUniqueId = this.getUniqueItemId(item.id, item.variantId);
        return itemUniqueId === uniqueId;
      });
    }
  
    isItemInQuoteList(uniqueId) {
      return !!this.findItemInQuoteList(uniqueId);
    }
  
    updateCardCheckboxState(productCard, variantInfo) {
      const checkbox = productCard.querySelector('.quote-checkbox');
      if (!checkbox) return;
  
      const productId = checkbox.dataset.productId;
      if (!productId) return;
  
      const uniqueId = this.getUniqueItemId(productId, variantInfo.id);
      const isInList = this.isItemInQuoteList(uniqueId);
      
      checkbox.checked = isInList;
    }
  
    handleButtonClick(button) {
      const productData = this.getProductDataFromButton(button);
      
      // Get variant info for this button
      const variantInfo = this.getCurrentVariantInfo(null, button);
      
      // Create complete item data with variant info
      const itemData = {
        ...productData,
        variantId: variantInfo.id,
        variantTitle: variantInfo.title,
        variantImage: variantInfo.image
      };
      
      // Create unique ID for this specific product+variant combination
      const uniqueId = this.getUniqueItemId(productData.id, variantInfo.id);
      
      // Check if this specific variant is already in the list
      const existingItem = this.findItemInQuoteList(uniqueId);
      
      if (existingItem) {
        // Voeg quantity toe aan bestaand item (same product + same variant)
        const newTotalQuantity = existingItem.quantity + productData.quantity;
        this.updateQuoteListItemQuantity(existingItem.id, newTotalQuantity, existingItem.variantId);
      } else {
        // Voeg nieuw item toe (new product or new variant of existing product)
        this.addToQuoteList(itemData);
      }
      
      try {
        this.updateButtons();
      } catch (error) {
        console.error('❌ Error in updateButtons():', error);
        console.error('Error stack:', error.stack);
      }
      
      this.renderQuoteListDrawer();
      this.renderQuoteListPage(); // Maatwerk: render page if on quote list page
      this.updateHeaderCount();
      this.updateContactFormVisibility(); // Maatwerk: update contact form visibility
      this.updateClearButtonVisibility(); // Maatwerk: update clear button visibility
      this.showConfirmationMessage('Product', productData.quantity, variantInfo.title);
    }
  
    getProductDataFromCheckbox(checkbox) {
      return {
        id: checkbox.dataset.productId,
        url: checkbox.dataset.productUrl,
        title: checkbox.dataset.productTitle || 'Product',
        quantity: 1
      };
    }
  
    getProductDataFromButton(button) {
      return {
        id: button.dataset.productId,
        url: button.dataset.productUrl,
        title: button.dataset.productTitle || 'Product',
        quantity: this.getQuantityFromPage()
      };
    }
  
    getQuantityFromPage() {
      // Zoek quantity selector op de pagina - probeer verschillende selectoren
      const selectors = [
        'input[name="quantity"]',
        '.quantity-input', 
        'input[type="number"]',
        '#qty',
        '.qty-input',
        '[data-quantity-input]',
        '.product-form input[type="number"]',
        'form input[type="number"]'
      ];
      
      let quantityInput = null;
      
      // Probeer elke selector totdat we een input vinden
      for (const selector of selectors) {
        quantityInput = document.querySelector(selector);
        if (quantityInput) {
          break;
        }
      }
      
      if (quantityInput) {
        const quantity = parseInt(quantityInput.value) || 1;
        return Math.max(1, quantity); // Minimum 1
      }
      
      return 1; // Default quantity
    }
  
  
  
    updateQuoteListItemQuantity(productId, newQuantity, variantId = null) {
      let quoteList = this.getQuoteList();
      const uniqueId = this.getUniqueItemId(productId, variantId);
      
      const itemIndex = quoteList.findIndex(item => {
        const itemUniqueId = this.getUniqueItemId(item.id, item.variantId);
        return itemUniqueId === uniqueId;
      });
      
      if (itemIndex !== -1) {
        quoteList[itemIndex].quantity = newQuantity;
        this.saveQuoteList(quoteList);
      }
    }
  
    showConfirmationMessage(productLabel, quantity, variantTitle = null) {
      // Toon korte bevestiging (kan later worden uitgebreid)
      const itemLabel = variantTitle ? `${productLabel} - ${variantTitle}` : productLabel;
      
      // Optioneel: Toon toast notification
      // Dit kan later worden uitgebreid met een visuele melding
    }
  
    addToQuoteList(productData) {
      let quoteList = this.getQuoteList();
      
      // Create unique ID for this specific product+variant combination
      const uniqueId = this.getUniqueItemId(productData.id, productData.variantId);
      
      // Check if this specific product+variant combination already exists
      const existingItem = quoteList.find(item => {
        const itemUniqueId = this.getUniqueItemId(item.id, item.variantId);
        return itemUniqueId === uniqueId;
      });
      
      if (existingItem) {
        return; // This specific product+variant is already in the list
      }
      
      // Check maximum aantal items
      if (quoteList.length >= this.maxItems) {
        alert(`Je kunt maximaal ${this.maxItems} producten toevoegen aan je offertelijst.`);
        return;
      }
      
      // Voeg product toe met ALLE data inclusief variant info
      const newItem = {
        id: productData.id,
        url: productData.url,
        title: productData.title || 'Product',
        quantity: productData.quantity || 1,
        variantId: productData.variantId || null,
        variantTitle: productData.variantTitle || null,
        variantImage: productData.variantImage || null
      };
      
      quoteList.push(newItem);
      
      this.saveQuoteList(quoteList);
    }
  
    removeFromQuoteList(uniqueId) {
      let quoteList = this.getQuoteList();
      
      // For backward compatibility, try both unique ID and regular product ID
      const initialLength = quoteList.length;
      
      // First try to find by unique ID (product_variant format)
      quoteList = quoteList.filter(item => {
        const itemUniqueId = this.getUniqueItemId(item.id, item.variantId);
        return itemUniqueId !== uniqueId;
      });
      
      // If no items were removed and uniqueId doesn't contain underscore, try regular product ID
      if (quoteList.length === initialLength && !uniqueId.includes('_')) {
        quoteList = quoteList.filter(item => item.id !== uniqueId);
      }
      
      this.saveQuoteList(quoteList);
    }
  
    getQuoteList() {
      try {
        const stored = localStorage.getItem(this.storageKey);
        return stored ? JSON.parse(stored) : [];
      } catch (error) {
        console.error('Error reading quote list from localStorage:', error);
        return [];
      }
    }
  
    saveQuoteList(quoteList) {
      try {
        localStorage.setItem(this.storageKey, JSON.stringify(quoteList));
      } catch (error) {
        console.error('Error saving quote list to localStorage:', error);
      }
    }
  
    isInQuoteList(productId) {
      const quoteList = this.getQuoteList();
      return quoteList.some(item => item.id === productId);
    }
  
    updateCheckboxStates() {
      const checkboxes = document.querySelectorAll('.quote-checkbox');
      checkboxes.forEach(checkbox => {
        const productId = checkbox.dataset.productId;
        if (!productId) return;
        
        // Get variant info - pass checkbox so its data attributes are checked first
        const productCard = checkbox.closest('.product-item, .product-card, .l4c, li[data-product-id], article[data-product-id], .twn-starter__products-grid-item, .twn-product-tile');
        const variantInfo = this.getCurrentVariantInfo(productCard, null, checkbox);
        
        // Check if this specific variant is in the list
        const uniqueId = this.getUniqueItemId(productId, variantInfo.id);
        const isInList = this.isItemInQuoteList(uniqueId);
        checkbox.checked = isInList;
        
        // Update visual states
        this.updateCheckboxVisuals(checkbox, isInList);
      });
    }
  
    syncAllCheckboxes(productId, checked) {
      // Zorg ervoor dat alle checkboxes voor hetzelfde product synchroon zijn
      const checkboxes = document.querySelectorAll(`.quote-checkbox[data-product-id="${productId}"]`);
      checkboxes.forEach(checkbox => {
        checkbox.checked = checked;
        this.updateCheckboxVisuals(checkbox, checked);
      });
    }
  
    updateCheckboxVisuals(checkbox, isInList) {
      let hideSpans, showSpans;

      // Method 1: standard label[for] (product-item.liquid structure)
      if (checkbox.id) {
        const label = document.querySelector(`label[for="${checkbox.id}"]`);
        if (label) {
          hideSpans = label.querySelectorAll('[data-checked-hide]');
          showSpans = label.querySelectorAll('[data-checked-show]');
        }
      }

      // Method 2: fallback for tweakwise grid (spans are siblings in parent container)
      if (!hideSpans || !hideSpans.length) {
        const parent = checkbox.closest('.twn-product-tile__quotation-link, .syk-jako-previously-bought-container, p.check');
        if (parent) {
          hideSpans = parent.querySelectorAll('[data-checked-hide]');
          showSpans = parent.querySelectorAll('[data-checked-show]');
        }
      }

      if (!hideSpans || !hideSpans.length) return;

      hideSpans.forEach(span => {
        span.style.display = isInList ? 'none' : 'inline';
      });
      
      showSpans.forEach(span => {
        span.style.display = isInList ? 'inline' : 'none';
      });
    }
  
    restoreCheckboxes() {
      // Herstel checkbox states na page load
      setTimeout(() => {
        this.updateCheckboxStates();
      }, 100);
    }
  
    updateButtons() {
      const buttons = document.querySelectorAll('.quote-button');
      
      buttons.forEach(button => {
        const productId = button.dataset.productId;
        
        // Get variant info for this button (from data attributes)
        const variantInfo = this.getCurrentVariantInfo(null, button);
        
        if (variantInfo.id) {
          // Button has specific variant info - check for that exact variant
          const uniqueId = this.getUniqueItemId(productId, variantInfo.id);
          
          const specificItem = this.findItemInQuoteList(uniqueId);
          
          const buttonText = button.querySelector('.quote-button-text');
          if (buttonText) {
            const addText = button.dataset.textAdd || 'Toevoegen aan offertelijst';
            const addedText = button.dataset.textAdded || '{quantity}x op offertelijst';
            
            if (specificItem) {
              // This specific variant is in the list
              const finalText = addedText.replace('{quantity}', specificItem.quantity);
              buttonText.textContent = finalText;
              button.classList.add('in-quote-list');
            } else {
              // This specific variant is not in the list - show add button
              buttonText.textContent = addText;
              button.classList.remove('in-quote-list');
            }
          }
        } else {
          // Button has no variant info - check if any variant of this product is in the list
          const quoteList = this.getQuoteList();
          let totalQuantity = 0;
          let foundItems = [];
          
          // Find all items for this product (with or without variants)
          quoteList.forEach(item => {
            if (item.id === productId) {
              totalQuantity += item.quantity;
              foundItems.push(item);
            }
          });
          
          const buttonText = button.querySelector('.quote-button-text');
          if (buttonText) {
            const addText = button.dataset.textAdd || 'Toevoegen aan offertelijst';
            const addedText = button.dataset.textAdded || '{quantity}x op offertelijst';
            
            if (foundItems.length > 0) {
              // Er zijn items van dit product in de lijst
              const finalText = addedText.replace('{quantity}', totalQuantity);
              buttonText.textContent = finalText;
              button.classList.add('in-quote-list');
            } else {
              // Geen items van dit product in de lijst
              buttonText.textContent = addText;
              button.classList.remove('in-quote-list');
            }
          }
        }
      });
    }
  
    toggleQuoteListDrawer() {
      const drawer = document.getElementById('quote-list');
      if (!drawer) return;
      
      const isOpen = drawer.getAttribute('aria-hidden') === 'false';
      
      if (isOpen) {
        // Close drawer
        drawer.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('quote-list-open');
      } else {
        // Open drawer
        drawer.setAttribute('aria-hidden', 'false');
        document.body.classList.add('quote-list-open');
        this.renderQuoteListDrawer(); // Refresh content when opening
      }
    }
  
    formatMoney(cents) {
      if (cents === null || cents === undefined || cents === '') return '';
      const euros = (parseFloat(cents) / 100).toFixed(2);
      return '€' + euros.replace('.', ',');
    }

    // Helper function to render quote list items (shared between drawer and page)
    renderQuoteListItems(container, quoteList, onComplete = null) {
      if (!container) return;

      // Loading state
      container.classList.add('loading');
      
      // Render products via section rendering API
      let quoteListHtml = '';
      let itemsDone = 0;
      const quoteListProductsObj = {};
      const quoteListPricesObj = {};
      
      quoteList.forEach((item, index) => {
        const baseUrl = item.url.split('?')[0];
        const fetchUrl = baseUrl + '?section_id=product-item-quote-list';
        fetch(fetchUrl)
          .then((response) => {
            if (!response.ok) {
              return '';
            }
            return response.text();
          })
          .then((text) => {
            if (text) {
              const resultsMarkup = new DOMParser().parseFromString(text, 'text/html');
              const sectionElement = resultsMarkup.getElementById('shopify-section-product-item-quote-list');
              
              if (sectionElement) {
                let itemHtml = sectionElement.innerHTML;
                
                // Extract price from data attribute (in cents)
                const liElement = sectionElement.querySelector('[data-variant-price]');
                const variantPrice = liElement ? parseInt(liElement.getAttribute('data-variant-price')) || 0 : 0;
                const uniqueKey = this.getUniqueItemId(item.id, item.variantId);
                quoteListPricesObj[uniqueKey] = variantPrice;

                // Calculate and insert line total
                const lineTotal = variantPrice * item.quantity;
                itemHtml = itemHtml.replace(
                  /<p class="quote-item-line-total"[^>]*><\/p>/,
                  `<p class="quote-item-line-total" style="white-space: nowrap; font-weight: 600;">${this.formatMoney(lineTotal)}</p>`
                );

                // Update quantity in the rendered HTML input
                itemHtml = itemHtml.replace(
                  /value="1"/,
                  `value="${item.quantity}"`
                );
                
                // Ensure data-product-id is set correctly
                itemHtml = itemHtml.replace(
                  /data-product-id="[^"]*"/,
                  `data-product-id="${item.id}"`
                );
                
                // Add variant ID to quantity input if variant exists
                if (item.variantId) {
                  itemHtml = itemHtml.replace(
                    /<input([^>]*class="quote-quantity-input"[^>]*)(>)/,
                    `<input$1 data-variant-id="${item.variantId}"$2`
                  );
                }
                
                // Add variant ID to remove button if variant exists
                if (item.variantId) {
                  const uniqueId = this.getUniqueItemId(item.id, item.variantId);
                  itemHtml = itemHtml.replace(
                    /data-product-id="[^"]*"/g,
                    `data-product-id="${uniqueId}"`
                  );
                }
                
                // Add variant title display if variant exists
                if (item.variantTitle) {
                  itemHtml = itemHtml.replace(
                    /<p class="variant-info size-12"[^>]*><\/p>/,
                    `<p class="variant-info size-12" style="display: block; margin-top: 4px; color: #666;">${item.variantTitle}</p>`
                  );
                }
                
                // Update product link to include variant parameter if variant exists
                if (item.variantId) {
                  itemHtml = itemHtml.replace(
                    /<a([^>]*class="[^"]*quote-product-link[^"]*"[^>]*href=")[^"]*(")/,
                    `<a$1${item.url}?variant=${item.variantId}$2`
                  );
                }
                
                // Update product image to variant image if available
                if (item.variantImage) {
                  itemHtml = itemHtml.replace(
                    /<img([^>]*class="quote-item-image"[^>]*src=")[^"]*(")/,
                    `<img$1${item.variantImage}$2`
                  );
                  // Remove srcset when using variant image (simpler approach)
                  itemHtml = itemHtml.replace(
                    /<img([^>]*class="quote-item-image"[^>]*)(\s+srcset="[^"]*")/,
                    `<img$1`
                  );
                }
                
                quoteListProductsObj[uniqueKey] = itemHtml;
              }
            }
            
            itemsDone++;
            if (itemsDone === quoteList.length) {
              // Build final HTML in correct order
              quoteList.forEach(item => {
                const uniqueKey = this.getUniqueItemId(item.id, item.variantId);
                if (quoteListProductsObj[uniqueKey]) {
                  quoteListHtml += quoteListProductsObj[uniqueKey];
                }
              });
              
              container.innerHTML = quoteListHtml;

              // Calculate and render grand total
              let grandTotal = 0;
              quoteList.forEach(item => {
                const uniqueKey = this.getUniqueItemId(item.id, item.variantId);
                const price = quoteListPricesObj[uniqueKey] || 0;
                grandTotal += price * item.quantity;
              });
              this.renderGrandTotal(container, grandTotal);

              container.classList.remove('loading');
              
              // Dispatch semantic input event for theme functionality
              if (typeof semanticInputEvt !== 'undefined') {
                window.dispatchEvent(semanticInputEvt);
              }
              
              // Add direct event listeners to quantity inputs as fallback
              const quantityInputs = container.querySelectorAll('.quote-quantity-input');
              quantityInputs.forEach((input) => {
                const self = this; // Ensure correct context
                
                input.addEventListener('change', function(e) {
                  self.handleQuantityChange(e.target);
                });
                
                input.addEventListener('input', function(e) {
                  self.handleQuantityChange(e.target);
                });
                
                input.addEventListener('keyup', function(e) {
                  self.handleQuantityChange(e.target);
                });
              });

              // Call completion callback if provided
              if (onComplete) {
                onComplete(quoteList);
              }
            }
          })
          .catch((error) => {
            itemsDone++;
            if (itemsDone === quoteList.length) {
              container.classList.remove('loading');
              if (onComplete) {
                onComplete(quoteList);
              }
            }
          });
      });
    }

    renderGrandTotal(container, totalCents) {
      // Find or create the total element after the container
      const parent = container.parentElement;
      if (!parent) return;

      let totalEl = parent.querySelector('.quote-list-total');
      if (!totalEl) {
        totalEl = document.createElement('div');
        totalEl.className = 'quote-list-total';
        totalEl.style.cssText = 'padding: 16px 15px 32px; border-top: 1px solid var(--border_color, #e5e5e5); display: flex; justify-content: space-between; align-items: center; font-weight: 600;';
        container.insertAdjacentElement('afterend', totalEl);
      }

      totalEl.innerHTML = `<span>Totaal</span><span>${this.formatMoney(totalCents)}</span>`;
    }
  
    renderQuoteListDrawer() {
      const quoteList = this.getQuoteList();
      const drawer = document.getElementById('quote-list');
      
      if (!drawer) return;
      
      const container = drawer.querySelector('.quote-list-items');
      const emptyMessage = drawer.querySelector('.quote-list-empty');
      const actionsPanel = drawer.querySelector('.quote-list-actions'); // Maatwerk: sticky buttons panel
      
      if (!container) return;
      
      // Show/hide empty message and drawer visibility
      if (quoteList.length === 0) {
        if (emptyMessage) emptyMessage.style.display = 'block';
        if (actionsPanel) actionsPanel.style.display = 'none'; // Maatwerk: hide actions when empty
        container.innerHTML = '';
        container.style.display = 'none';
        // Remove grand total when empty
        const totalEl = container.parentElement?.querySelector('.quote-list-total');
        if (totalEl) totalEl.remove();
        return;
      }
      
      if (emptyMessage) emptyMessage.style.display = 'none';
      if (actionsPanel) actionsPanel.style.display = 'block'; // Maatwerk: show actions when products exist
      container.style.display = 'block';
      
      // Use helper function to render quote list items
      this.renderQuoteListItems(container, quoteList);
    }
  
    // Public API methods
    getQuoteListCount() {
      const quoteList = this.getQuoteList();
      // Calculate total quantity (sum of all product quantities)
      return quoteList.reduce((total, item) => total + (item.quantity || 1), 0);
    }
  
    // Test function to manually check quantity inputs
    testQuantityInputs() {
      const drawer = document.getElementById('quote-list');
      if (!drawer) return;
      
      const container = drawer.querySelector('.quote-list-items');
      if (!container) return;
      
      const quantityInputs = container.querySelectorAll('.quote-quantity-input');
      
      quantityInputs.forEach((input) => {
        // Try to programmatically change value and trigger events
        const originalValue = input.value;
        input.value = '999';
        
        // Trigger events manually
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
  
    clearQuoteList() {
      localStorage.removeItem(this.storageKey);
      this.updateCheckboxStates();
      this.updateButtons();
      this.renderQuoteListDrawer();
      this.renderQuoteListPage(); // Maatwerk: render page if on quote list page
      this.updateHeaderCount();
      this.updateContactFormVisibility(); // Maatwerk: update contact form visibility
      this.updateClearButtonVisibility(); // Maatwerk: update clear button visibility
    }
  
    getQuoteListItems() {
      return this.getQuoteList();
    }
  
    // Method om offertelijst data te formatteren voor verzending
    getQuoteListForSubmission() {
      const quoteList = this.getQuoteList();
      return quoteList.map(item => ({
        productId: item.id,
        url: item.url,
        quantity: item.quantity || 1
      }));
    }
  
    // Public API methods voor quantity support
    getTotalQuantity() {
      const quoteList = this.getQuoteList();
      return quoteList.reduce((total, item) => total + (item.quantity || 1), 0);
    }
  
    getQuoteListWithQuantities() {
      return this.getQuoteList();
    }
  
    // Header count update method
    updateHeaderCount() {
      const headerCount = document.getElementById('quote-list-count');
      if (!headerCount) return;
      
      const count = this.getQuoteListCount();
      headerCount.textContent = count;
      
      if (count > 0) {
        headerCount.style.display = 'block';
      } else {
        headerCount.style.display = 'none';
      }
    }
  
    // Drawer control methods
    openQuoteListDrawer() {
      const drawer = document.getElementById('quote-list');
      if (!drawer) return;
      
      drawer.setAttribute('aria-hidden', 'false');
      document.body.classList.add('quote-list-open');
      this.renderQuoteListDrawer();
    }
  
    closeQuoteListDrawer() {
      const drawer = document.getElementById('quote-list');
      if (!drawer) return;
      
      drawer.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('quote-list-open');
    }
  
    // Maatwerk: renderQuoteListPage method - renders quote list on dedicated page
    renderQuoteListPage() {
      const quoteList = this.getQuoteList();
      const pageContainer = document.querySelector('.quote-list-page-container');
      const container = document.querySelector('.quote-list-items-page');
      const emptyMessage = pageContainer ? pageContainer.querySelector('.quote-list-empty') : null;
      
      // Only render if we're on the quote list page
      if (!container || !pageContainer) {
        return;
      }
      
      // Show/hide empty message and container visibility
      if (quoteList.length === 0) {
        if (emptyMessage) {
          emptyMessage.style.display = 'block';
        }
        container.innerHTML = '';
        container.style.display = 'none'; // Hide container when empty
        // Remove grand total when empty
        const totalEl = container.parentElement?.querySelector('.quote-list-total');
        if (totalEl) totalEl.remove();
        this.updateFormData([]); // Clear form data when empty
        return;
      }
      
      if (emptyMessage) {
        emptyMessage.style.display = 'none';
      }
      container.style.display = 'block'; // Show container when not empty
      
      // Use helper function to render quote list items with callback for form data update
      this.renderQuoteListItems(container, quoteList, (renderedQuoteList) => {
        // Update hidden form field with quote list data (page-specific functionality)
        this.updateFormData(renderedQuoteList);
      });
    }
  
    // Maatwerk: updateFormData - updates hidden form field with quote list data
    updateFormData(quoteList) {
      const formDataField = document.getElementById('quote-list-data');
      if (!formDataField) return;
      
      const formData = quoteList.map(item => {
        const productInfo = `${item.title || 'Product'} (ID: ${item.id}`;
        const variantInfo = item.variantId && item.variantTitle ? `, Variant: ${item.variantTitle} - ID: ${item.variantId}` : '';
        const quantityInfo = `, Quantity: ${item.quantity})`;
        return productInfo + variantInfo + quantityInfo;
      }).join('\n');
      
      formDataField.value = formData;
    }
  
    // Maatwerk: updateContactFormVisibility - shows/hides contact form based on quote list status
    updateContactFormVisibility() {
      if (typeof window.updateQuoteContactForm === 'function') {
        window.updateQuoteContactForm();
      }
    }
  
    // Maatwerk: updateClearButtonVisibility - shows/hides clear button on quote list page based on list status
    updateClearButtonVisibility() {
      if (typeof window.updateQuoteClearButton === 'function') {
        window.updateQuoteClearButton();
      }
    }
  
    // Maatwerk: getClearConfirmSettings - gets confirm dialog settings based on button context
    getClearConfirmSettings(buttonElement) {
      const button = buttonElement.classList.contains('quote-clear-all') || buttonElement.classList.contains('quote-clear-all-page') 
        ? buttonElement 
        : buttonElement.closest('.quote-clear-all, .quote-clear-all-page');
      
      if (!button) {
        return { showConfirm: true, confirmText: 'Weet je zeker dat je de hele offertelijst wilt legen?' };
      }
  
      // Check if this is a drawer button (quote-clear-all) or page button (quote-clear-all-page)
      if (button.classList.contains('quote-clear-all')) {
        // Drawer button - find the drawer section
        const drawerSection = button.closest('#quote-list');
        if (drawerSection) {
          const sectionId = drawerSection.getAttribute('data-section-id') || 
                           drawerSection.className.match(/shopify-section-([^\s]+)/)?.[1];
          
          if (sectionId) {
            // Try to get settings from the section element's data attributes or window object
            const showConfirm = this.getSectionSetting(sectionId, 'show_clear_confirm', true);
            const confirmText = this.getSectionSetting(sectionId, 'clear_confirm_text', 'Weet je zeker dat je de hele offertelijst wilt legen?');
            
            return { showConfirm, confirmText };
          }
        }
        return { showConfirm: true, confirmText: 'Weet je zeker dat je de hele offertelijst wilt legen?' };
      } 
      else if (button.classList.contains('quote-clear-all-page')) {
        // Page button - find the page section
        const pageSection = button.closest('.section-quote-list, [class*="shopify-section"]');
        if (pageSection) {
          const sectionId = pageSection.getAttribute('data-section-id') || 
                           pageSection.className.match(/shopify-section-([^\s]+)/)?.[1];
          
          if (sectionId) {
            // Try to get settings from the section element's data attributes or window object
            const showConfirm = this.getSectionSetting(sectionId, 'show_clear_confirm', true);
            const confirmText = this.getSectionSetting(sectionId, 'clear_confirm_text', 'Weet je zeker dat je de hele offertelijst wilt legen?');
            
            return { showConfirm, confirmText };
          }
        }
        return { showConfirm: true, confirmText: 'Weet je zeker dat je de hele offertelijst wilt legen?' };
      }
  
      // Fallback
      return { showConfirm: true, confirmText: 'Weet je zeker dat je de hele offertelijst wilt legen?' };
    }
  
    // Helper method to get section settings
    getSectionSetting(sectionId, settingName, defaultValue) {
      // Try multiple approaches to get section settings
      
      // Method 1: Check if there's a global sections object
      if (window.theme && window.theme.sections && window.theme.sections[sectionId]) {
        const setting = window.theme.sections[sectionId][settingName];
        if (setting !== undefined) return setting;
      }
      
      // Method 2: Check section element data attributes
      const sectionElement = document.querySelector(`[data-section-id="${sectionId}"], .shopify-section-${sectionId}`);
      if (sectionElement) {
        const dataAttr = sectionElement.getAttribute(`data-${settingName.replace(/_/g, '-')}`);
        if (dataAttr !== null) {
          // Convert string values to appropriate types
          if (dataAttr === 'true') return true;
          if (dataAttr === 'false') return false;
          return dataAttr;
        }
      }
      
      // Method 3: Fallback to default
      return defaultValue;
    }
  }
  
  // Initialize the quote list when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      window.QuoteList = new QuoteList();
    });
  } else {
    window.QuoteList = new QuoteList();
  }
  
  // Export voor gebruik in andere scripts
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = QuoteList;
  }
  